import { createIntegrationConnectionSettingsApi } from "../../api/integration-connection-settings-api.js";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Cable,
  ExternalLink,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  ActionMenu,
  Alert,
  AsyncSection,
  Badge,
  Button,
  Card,
  DescriptionItem,
  DescriptionList,
  EmptyState,
  Input,
  Textarea,
  Modal,
  RightDrawer,
  SectionHeader,
  SettingsGroup,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  Skeleton,
  StatusPill,
  type ActionMenuItem,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import {
  useApiClient,
  type ConnectableIntegration,
  type IntegrationConnectionDetail,
} from "@playbookai/api-clients";
import { getDocumentationUrl } from "../../config/runtime.js";
import {
  SETTINGS_CONNECTIONS_NEW_ROUTE,
  SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE,
} from "../../routes.js";
import {
  useDateTimeFormatter,
  type DateTimeFormatter,
} from "../../settings/format-date-time.js";
import { offersReauthorize } from "./reauthorize.js";
import { connectRefusalMessage } from "./connect-refusal.js";
import { useConnectionAuthorization } from "./use-connection-authorization.js";

/**
 * PB-1592 (EI-04) — a user's own enterprise connections.
 *
 * PERSONAL, not administrative, and that placement is the design. EI-03's
 * `Integrations` section is tenant-admin and configures what the WORKSPACE may
 * reach; this one is every member managing their OWN credentials, so it sits in
 * the personal nav beside PlayerAI and Notifications with no admin gate. A
 * member who cannot configure an integration must still be able to connect to
 * one their administrator set up.
 *
 * Provider-neutral like every other integration surface: the instances come
 * from the tenant's configured list and are rendered by their administrator-
 * chosen names. Provider branding is presentation-only and always has a
 * generic fallback; no provider changes connection behavior in this file.
 *
 * THE ROUND TRIP DOES NOT COME BACK HERE. The provider redirects to the backend
 * callback, which sends that tab to the PUBLIC `/oauth/integration-complete`
 * page — this one stays open and authenticated throughout. That page announces
 * a contentless completion signal; `useOauthCompletionWatcher` hears it, and
 * the answer to "what happened" comes from refetching the two lists below, not
 * from the message. See `pages/oauth/completion-channel.ts` for why it
 * deliberately carries nothing.
 */
export function ConnectionsSettings(): ReactElement {
  const config = useApiClient();
  const api = useMemo(
    () => createIntegrationConnectionSettingsApi(config),
    [config],
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const connections = useQuery({
    queryKey: ["integration-connections"],
    queryFn: () => api.listMine(),
  });
  // PB-4734. This used to call the tenant-ADMIN list and swallow its 403 as
  // "nothing to connect to" — which was true for an administrator and false for
  // everyone else: a member who had been granted access still saw an empty
  // section, and the member flow had no entrance at all. `listConnectable` is
  // member-callable and answers the question this page actually asks — what may
  // I connect to — returning only instances the caller is granted AND that are
  // enabled, so the enabled filter below is now the server's job too.
  const instances = useQuery({
    queryKey: ["connectable-integrations"],
    queryFn: () => api.listConnectable(),
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ["integration-connections"],
    });
  };

  /**
   * What "the round trip finished" means on this page: ask the server again.
   *
   * BOTH lists, because a round trip can change both — a new connection appears
   * in the first, and the second is the server's own answer to what this member
   * may connect to, which is the list the Connect buttons are rendered from.
   *
   * This is the authority. The completion tab's signal carries no outcome, no
   * connection id and no error code precisely so that nothing on this page can
   * be decided by an unauthenticated page's say-so; these two authenticated
   * reads decide it instead.
   */
  const refreshAfterAuthorization = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["integration-connections"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["connectable-integrations"],
      }),
    ]);
  };

  const authorization = useConnectionAuthorization(
    api,
    refreshAfterAuthorization,
  );

  const documentationUrl = getDocumentationUrl();
  // Verifiable as written: credential material lives in the secret store
  // (Azure Key Vault in production; Postgres holds only a reference), a
  // connection acts only for the person who made it, and Disconnect revokes it.
  const meta = (
    <SettingsMetaCard
      icon={ShieldCheck}
      title="Security"
      description={`Access tokens are stored encrypted and used only for actions you take in ${PRODUCT_NAME}. Disconnecting revokes them immediately.`}
      {...(documentationUrl
        ? {
            links: [
              {
                label: "How connections work",
                icon: BookOpen,
                href: documentationUrl,
                external: true,
                accessory: <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />,
              },
            ],
          }
        : {})}
    />
  );
  const header = (
    <SectionHeader
      variant="settings"
      leading={<Cable className="w-4 h-4 text-pb-cyan-text" />}
      title="Your connections"
      description={`Connect and manage the accounts you use with ${PRODUCT_NAME}.`}
      actionsSlot={
        <div className="flex shrink-0 items-center gap-2">
          <SettingsDetailsButton meta={meta} />
          <Button
            variant="primary"
            onClick={() => navigate(SETTINGS_CONNECTIONS_NEW_ROUTE)}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add new connection
          </Button>
        </div>
      }
    />
  );

  if (connections.isLoading || instances.isLoading) {
    return (
      <div>
        {header}
        <SettingsPageLayout meta={meta} mainWidth="fluid">
          {/* The wait through AsyncSection's loading branch, named "Loading
              your connections". A failed read renders the page below. */}
          <AsyncSection
            status="loading"
            loadingLabel="your connections"
            errorTitle="Couldn't load your connections"
            skeleton={
              <div className="flex flex-col gap-3">
                <Skeleton className="h-8 w-48" />
                <Skeleton shape="card" className="h-36" />
                <Skeleton shape="card" className="h-36" />
              </div>
            }
          >
            {null}
          </AsyncSection>
        </SettingsPageLayout>
      </div>
    );
  }

  // No `enabled` filter any more: the server only returns instances that are
  // both granted and enabled, and re-deciding it here would be a second copy of
  // a rule that has to agree with `requireUsableInstance` to be worth anything.
  const connectable = instances.data ?? [];

  const existingConnections = connections.data ?? [];

  return (
    <div className="pb-motion-content-enter">
      {header}
      <SettingsPageLayout meta={meta} mainWidth="fluid">
        {/* The authorization happens in another tab, so this page would
            otherwise look inert while the user is busy elsewhere — and then
            change on its own when they came back. Saying so is what makes the
            later update read as the flow working rather than as a glitch. It
            claims no outcome: the lists below are the only report. The teal
            callout is an info Alert (OK-35), a status rather than an alert. */}
        {authorization.watching ? (
          <Alert tone="info" role="status" className="mb-5">
            Waiting for the authorization tab. This page updates itself when the
            connection is finished.
          </Alert>
        ) : null}

        {authorization.error !== null ? (
          <div className="mb-5">
            <ConnectRefusal error={authorization.error} />
          </div>
        ) : null}
        {/* Kept as today's red line: Alert (inline included) adds a glyph
            and its own type, a look change no PR 3 row approves. */}
        {authorization.windowError !== null ? (
          <p role="alert" className="mb-5 text-body-small text-pb-red-text">
            {authorization.windowError}
          </p>
        ) : null}

        <SettingsGroup variant="settings" title="Connected accounts">
          <div className="mt-3">
            {connections.isError ? (
              // A failed read is not "no connections yet" — that claim tells a
              // user their accounts are gone when the page simply could not ask.
              // The Retry is the Alert's trailing action (OK-34).
              <Alert
                tone="critical"
                title="Couldn't load your connections"
                actionsSlot={
                  /* A button, not an instruction to reload the browser. */
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void connections.refetch();
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                {(connections.error as Error | null)?.message}
              </Alert>
            ) : existingConnections.length === 0 ? (
              /* The framed empty state: EmptyState on the dashed Card. */
              <Card variant="dashed">
                <EmptyState
                  icon={<Cable className="h-5 w-5" />}
                  title="No connections yet"
                  description="Add a connection to get started."
                  action={
                    /* Secondary: the page header already offers this exact action,
                   accented, a few centimetres above. Two identical purple
                   buttons for one action is the defect §9 exists to catch. */
                    <Button
                      variant="secondary"
                      onClick={() => navigate(SETTINGS_CONNECTIONS_NEW_ROUTE)}
                    >
                      Add new connection
                    </Button>
                  }
                />
              </Card>
            ) : (
              <ul className="flex flex-col gap-3">
                {existingConnections.map((connection) => {
                  const integration = connectable.find(
                    (candidate) =>
                      candidate.id === connection.tenantIntegrationId,
                  );
                  return (
                    <ConnectionCard
                      key={connection.id}
                      connection={connection}
                      {...(integration === undefined ? {} : { integration })}
                      api={api}
                      onChanged={invalidate}
                      // The SAME mutation the Connect button uses. Re-authorizing
                      // is not a distinct operation — it is authorizing again.
                      onReauthorize={authorization.start}
                      reauthorizing={authorization.isPending}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        </SettingsGroup>
      </SettingsPageLayout>
    </div>
  );
}

type ConnectionPanel = "details" | null;

function ConnectionCard({
  connection,
  integration,
  api,
  onChanged,
  onReauthorize,
  reauthorizing,
}: {
  connection: IntegrationConnectionDetail;
  integration?: ConnectableIntegration;
  api: ReturnType<typeof createIntegrationConnectionSettingsApi>;
  onChanged: () => void | Promise<void>;
  onReauthorize: (tenantIntegrationId: string) => void;
  reauthorizing: boolean;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  const navigate = useNavigate();
  const [editingDescription, setEditingDescription] = useState(false);
  const [draftDescription, setDraftDescription] = useState(
    connection.description ?? "",
  );
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const updateDescription = useMutation({
    mutationFn: () =>
      api.updateDescription(connection.id, draftDescription.trim()),
    onSuccess: async () => {
      setEditingDescription(false);
      await onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.remove(connection.id),
    onSuccess: async () => {
      setConfirmingRemove(false);
      await onChanged();
    },
  });
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(connection.displayName);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [panel, setPanel] = useState<ConnectionPanel>(null);

  const rename = useMutation({
    // Trimmed at the boundary: a name of spaces is no name, and sending the
    // untrimmed value would store invisible whitespace a user cannot see.
    mutationFn: () => api.rename(connection.id, draftName.trim()),
    onSuccess: async () => {
      setRenaming(false);
      await onChanged();
    },
  });
  const validate = useMutation({
    mutationFn: () => api.validate(connection.id),
    onSuccess: onChanged,
  });
  const revoke = useMutation({
    mutationFn: () => api.revoke(connection.id),
    onSuccess: async () => {
      setConfirmingRevoke(false);
      await onChanged();
    },
  });
  const authorizeMcp = useMutation({
    mutationFn: () => api.authorizeMcp(connection.id),
    onSuccess: onChanged,
  });
  const verificationStarted = useRef(false);
  useEffect(() => {
    if (
      connection.status !== "active" ||
      connection.transportMode !== "mcp" ||
      connection.mcpValidatedAt !== null ||
      verificationStarted.current
    ) {
      return;
    }
    verificationStarted.current = true;
    authorizeMcp.mutate();
  }, [connection.id, connection.mcpValidatedAt, connection.status]);
  const mcpEnabled =
    connection.transportMode === "mcp" &&
    (authorizeMcp.data?.mcpValidatedAt != null ||
      connection.mcpValidatedAt !== null);
  const currentStatus = validate.data?.status ?? connection.status;
  const actionItems: readonly ActionMenuItem[] =
    connection.status === "revoked"
      ? [
          {
            id: "remove",
            label: "Remove",
            icon: Trash2,
            onSelect: () => {
              remove.reset();
              setConfirmingRemove(true);
            },
          },
        ]
      : [
          {
            id: "description",
            label: "Edit description",
            icon: Pencil,
            onSelect: () => {
              updateDescription.reset();
              setDraftDescription(connection.description ?? "");
              setEditingDescription(true);
            },
          },
          {
            id: "rename",
            label: "Rename",
            icon: Pencil,
            onSelect: () => {
              setDraftName(connection.displayName);
              setRenaming(true);
            },
          },
          {
            id: "check",
            label: validate.isPending
              ? "Checking connection…"
              : "Check connection",
            icon: RefreshCw,
            ...(validate.isPending
              ? { disabledReason: "A connection check is already running" }
              : { onSelect: () => validate.mutate() }),
          },
          {
            id: "disconnect",
            label: "Disconnect",
            icon: Trash2,
            onSelect: () => setConfirmingRevoke(true),
          },
        ];
  const providerName =
    integration?.displayName ??
    `${connection.transportMode.toUpperCase()} connection`;
  const providerPresentation = splitIntegrationName(providerName);

  const account =
    connection.externalPrincipalName?.trim() || connection.displayName;
  const products =
    integration?.description?.trim() || providerPresentation.context;
  const footerMeta =
    currentStatus === "pending"
      ? "Awaiting authorization"
      : `${connection.grantedScopes.length} ${connection.grantedScopes.length === 1 ? "permission" : "permissions"}${products ? ` · ${products}` : ""}`;

  return (
    <li>
      {/* Kept hand-built: Card paints its 1px inset top hairline
          (pb-hairline) on a flat default card, a look change no PR 3 row
          approves. The rows keep their own padding (px-4 py-3.75, the
          footer's py-2.75). */}
      <div className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card">
        <div className="flex items-start gap-3.25 px-4 py-3.75">
          <ProviderMark providerName={providerName} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.25">
              <h3 className="text-heading-3 text-pb-text">
                {providerPresentation.name}
              </h3>
              <StatusPill tone={statusTone(currentStatus)}>
                {statusLabel(currentStatus)}
              </StatusPill>
            </div>
            <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 text-body-small text-pb-text-secondary">
              {providerPresentation.context !== null ? (
                <>
                  <span className="truncate">
                    {providerPresentation.context}
                  </span>
                  <span aria-hidden>·</span>
                </>
              ) : null}
              <span className="truncate">Connected as {account}</span>
            </p>
            <p className="mt-1.25 text-caption text-pb-text-muted">
              {lastCheckedLabel(connection.lastVerifiedAt, formatter)}
            </p>
          </div>
          {
            <div className="flex shrink-0 items-center gap-2">
              <ActionMenu
                items={actionItems}
                triggerLabel={`Connection actions for ${connection.displayName}`}
              />
            </div>
          }
        </div>
        {connection.description ? (
          <p className="mx-4 mb-3.5 whitespace-pre-wrap break-words text-body-small text-pb-text-secondary">
            {connection.description}
          </p>
        ) : null}

        {connection.status === "active" &&
        connection.transportMode === "mcp" ? (
          mcpEnabled ? (
            <div className="mx-4 mb-3.5">
              <AccessPanel
                icon={Sparkles}
                title="Player AI"
                description="Choose which tools Player AI may use for you."
                onManage={() =>
                  navigate(
                    `${SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE}/${encodeURIComponent(connection.id)}`,
                  )
                }
              />
            </div>
          ) : (
            /* Kept hand-built, with its red line: Alert paints the full soft fill, a 40% border and a glyph
               (inline adds a glyph); no PR 3 look row approves that. */
            <div className="mx-4 mb-3.5 rounded-lg border border-pb-border bg-pb-card-elevated/50 px-3.25 py-2.75 text-caption text-pb-text-muted">
              {mcpEnabled
                ? "MCP is connected and its reviewed tools are available."
                : authorizeMcp.isError
                  ? "MCP authorization succeeded, but the managed server could not be verified."
                  : "Verifying the managed MCP server…"}
              {authorizeMcp.isError ? (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <p role="alert" className="text-pb-red-text">
                    MCP could not be verified. Check or reconnect this account.
                  </p>
                  <Button
                    size="sm"
                    disabled={authorizeMcp.isPending}
                    onClick={() => {
                      verificationStarted.current = true;
                      authorizeMcp.mutate();
                    }}
                  >
                    Retry verification
                  </Button>
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {/* PB-4734 (G5). `expired` and `failed` rendered as a bare badge and
          nothing else, so the one thing that fixes them — connecting again —
          was not offered anywhere on the surface that reports the problem. The
          user's next move was to guess.

          Not shown for `revoked`: that state is a deliberate disconnect, and
          re-authorizing from the same row would read as undo. The Connect
          button in the section above is the honest route back.

          It targets the connection's own instance, so the user does not have to
          work out which of several systems this row belonged to. */}
        {offersReauthorize({
          // The freshest status wins: a Check that just flipped this to `failed`
          // should offer the fix immediately, not after a refetch.
          status: validate.data?.status ?? connection.status,
        }) ? (
          /* Kept hand-built: Alert paints the full soft fill, a 40% border and a glyph,
             where this is a quiet elevated box; no PR 3 look row approves it. */
          <div className="mx-4 mb-3.5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-pb-border bg-pb-card-elevated/50 px-3.25 py-2.75">
            <p className="text-body-small text-pb-text-secondary">
              {currentStatus === "expired"
                ? "This connection has expired. Connecting again restores it."
                : "This connection stopped working. Connecting again restores it."}
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={reauthorizing}
              onClick={() => onReauthorize(connection.tenantIntegrationId)}
            >
              Reconnect
            </Button>
          </div>
        ) : null}

        {connection.status === "revoked" ? (
          // Revoking keeps the row so past work stays explicable. Saying so stops
          // the obvious next question — "why is this still here?". Kept
          // hand-built: an Alert box would add a glyph and a tone paint.
          <p className="mx-4 mb-3.5 rounded-lg border border-pb-border bg-pb-card-elevated/35 px-3.25 py-2.75 text-body-small text-pb-text-secondary">
            Disconnected. This account can no longer reach the provider. You can
            remove it from your connections; earlier activity is retained.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-pb-border/60 px-4 py-2.75">
          <Button variant="link" size="sm" onClick={() => setPanel("details")}>
            View connection details
            <ExternalLink className="h-3 w-3" aria-hidden />
          </Button>
          <span className="text-caption text-pb-text-muted">{footerMeta}</span>
        </div>
      </div>

      {/* The four failure lines below stay today's red text: Alert (inline
          included) adds a glyph and its own type, a look change no PR 3 row
          approves. */}

      {rename.isError ? (
        // A failed rename used to say NOTHING. The field stayed open, the row
        // kept its old name, and the Save button looked broken — which is the
        // one outcome a user cannot act on, because it is indistinguishable
        // from a dead control. Every other mutation on this row changes
        // something visible on success (revoke rewrites the row, check writes a
        // status), so rename was the only one that could fail in silence.
        <p role="alert" className="mt-2 text-xs text-pb-red-text">
          Could not rename this connection. Your changes were not saved — try
          again.
        </p>
      ) : null}

      {validate.isError ? (
        // The CHECK REQUEST failed — nothing was learned about the connection.
        // Distinct from the branch below, where the request succeeded and the
        // answer is "broken".
        <p role="alert" className="mt-2 text-xs text-pb-red-text">
          Could not check this connection. Try again.
        </p>
      ) : null}
      {revoke.isError ? (
        // The row is deliberately left as it was: a failed disconnect changed
        // nothing server-side, so hiding the connection would lie the other way.
        <p role="alert" className="mt-2 text-xs text-pb-red-text">
          Could not disconnect. The connection is unchanged — try again.
        </p>
      ) : null}
      {validate.data?.status === "failed" ? (
        // Announces the CHECK's result, and no longer instructs a disconnect
        // first: since PB-4734 the block above offers Reconnect directly for
        // exactly this status, so telling the user to disconnect sent them the
        // long way round — and, taken literally, through an irreversible step
        // they did not need.
        <p role="alert" className="mt-2 text-xs text-pb-red-text">
          The check failed — this connection is not working. Use Reconnect
          above.
        </p>
      ) : null}

      <Modal
        open={editingDescription}
        onClose={() => {
          if (!updateDescription.isPending) setEditingDescription(false);
        }}
        title="Connection description"
        description="Help Player AI choose this connection for the right work."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={updateDescription.isPending}
              onClick={() => setEditingDescription(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={updateDescription.isPending}
              onClick={() => updateDescription.mutate()}
            >
              Save description
            </Button>
          </>
        }
      >
        {/* Kept: FormField's label has its own type step, a look change no
            PR 3 row approves. */}
        <label
          htmlFor={`description-${connection.id}`}
          className="text-body-small"
        >
          When to use this connection (optional)
        </label>
        <Textarea
          id={`description-${connection.id}`}
          value={draftDescription}
          maxLength={1000}
          rows={4}
          onChange={(event) => setDraftDescription(event.target.value)}
          placeholder="Use for engineering bugs, development tasks and release tracking."
        />
        <p className="mt-2 text-caption text-pb-text-muted">
          Describe its purpose without including passwords or tokens. This does
          not change permissions.
        </p>
        {updateDescription.isError ? (
          <Alert tone="critical" title="Please try again">
            Could not save the description. Your text is kept — try again.
          </Alert>
        ) : null}
      </Modal>
      <Modal
        open={confirmingRemove}
        onClose={() => {
          if (!remove.isPending) setConfirmingRemove(false);
        }}
        title={`Remove ${connection.displayName}?`}
        description="Remove this disconnected entry from your connections."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => setConfirmingRemove(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-body-small text-pb-text-secondary">
          Earlier activity is retained. Connecting again creates a new entry.
        </p>
        {remove.isError ? (
          <Alert tone="critical" title="Please try again">
            Could not remove this connection. Refresh to check its status and
            try again.
          </Alert>
        ) : null}
      </Modal>
      <Modal
        open={renaming}
        onClose={() => setRenaming(false)}
        title="Rename connection"
        description="Use a name that helps you distinguish this account from your other connections."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={rename.isPending}
              disabled={draftName.trim() === ""}
              onClick={() => rename.mutate()}
            >
              Save name
            </Button>
          </>
        }
      >
        <Input
          value={draftName}
          aria-label="Connection name"
          onChange={(event) => setDraftName(event.target.value)}
        />
      </Modal>

      <Modal
        open={confirmingRevoke}
        onClose={() => setConfirmingRevoke(false)}
        title={`Disconnect ${connection.displayName}?`}
        description={`${PRODUCT_NAME} will no longer be able to use this account.`}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmingRevoke(false)}
            >
              Keep connected
            </Button>
            <Button
              variant="destructive"
              loading={revoke.isPending}
              onClick={() => revoke.mutate()}
            >
              Disconnect
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-pb-text-secondary">
          Earlier activity stays in {PRODUCT_NAME} for audit history. To use
          this account again, you will need to authorize it again.
        </p>
      </Modal>

      <RightDrawer
        open={panel !== null}
        onClose={() => setPanel(null)}
        title={panelTitle(panel)}
        subtitle={providerName}
        width={520}
      >
        {panel === "details" ? (
          <ConnectionDetails
            connection={connection}
            {...(integration === undefined ? {} : { integration })}
          />
        ) : null}
      </RightDrawer>
    </li>
  );
}

function AccessPanel({
  icon: Icon,
  title,
  description,
  onManage,
}: {
  icon: typeof Sparkles;
  title: string;
  description: string;
  onManage: () => void;
}): ReactElement {
  /* Kept hand-built, box and glyph tile: Alert tone="ai" paints the iris
     soft fill and border instead of the iris-interactive wash, and IconTile
     has no 30px step; no PR 3 look row approves either. */
  return (
    <section
      aria-label={`${title} access`}
      className="flex items-center gap-2.75 rounded-lg border border-pb-iris-interactive/35 bg-pb-iris-interactive/8 px-3.25 py-2.75"
    >
      <span className="flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-md bg-pb-iris-interactive/16 text-pb-iris-text">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-body-small font-semibold text-pb-text">{title}</h3>
        <p className="mt-px text-caption text-pb-text-secondary">
          {description}
        </p>
      </div>
      <Button
        variant="link"
        tone="ai"
        size="sm"
        className="shrink-0"
        onClick={onManage}
      >
        Manage <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </section>
  );
}

export function ProviderMark({
  providerName,
}: {
  providerName: string;
}): ReactElement {
  if (providerName.toLowerCase().startsWith("atlassian")) {
    return (
      <img
        src="/brand/atlassian-mark.svg"
        alt=""
        className="h-10 w-10 shrink-0 object-contain"
      />
    );
  }

  /* Kept: IconTile adds a border, the soft fill and the 8px corner to
     this 10px cyan wash; no PR 3 look row approves it. */
  return (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-pb-cyan/10 text-pb-cyan-text"
      aria-hidden
    >
      <Cable className="h-5 w-5" />
    </span>
  );
}

function ConnectionDetails({
  connection,
  integration,
}: {
  connection: IntegrationConnectionDetail;
  integration?: ConnectableIntegration;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  const details = [
    ["Account", connection.externalPrincipalName ?? connection.displayName],
    ["Connection name", connection.displayName],
    ["Connection type", connection.transportMode.toUpperCase()],
    ["Status", statusLabel(connection.status)],
    ["Last checked", checkedTimestamp(connection.lastVerifiedAt, formatter)],
    ["Connected", checkedTimestamp(connection.createdAt, formatter)],
    ["Connection ID", connection.id],
  ] as const;

  return (
    <div className="space-y-6 p-5">
      {/* The label/value grid is a DescriptionList (OK-25): the box goes,
          and the labels and values take the list's type. */}
      <DescriptionList layout="grid" divided>
        {details.map(([label, value]) => (
          <DescriptionItem key={label} label={label}>
            {value}
          </DescriptionItem>
        ))}
        {integration ? (
          <DescriptionItem label="Integration">
            {integration.displayName}
          </DescriptionItem>
        ) : null}
      </DescriptionList>
      <section aria-labelledby={`scopes-${connection.id}`}>
        <h3
          id={`scopes-${connection.id}`}
          className="text-sm font-semibold text-pb-text"
        >
          Granted permissions
        </h3>
        {/* Kept as today's 14px line: EmptyState size="sm" sets 11px
            type, a look change no PR 3 row approves. */}
        {connection.grantedScopes.length === 0 ? (
          <p className="mt-2 text-sm text-pb-text-muted">
            No permissions were reported.
          </p>
        ) : (
          <ul className="mt-3 flex flex-wrap gap-2">
            {connection.grantedScopes.map((scope) => (
              <li key={scope}>
                <Badge variant="outline" className="max-w-full break-all py-1">
                  {scope}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function panelTitle(panel: ConnectionPanel): string {
  if (panel === "details") return "Connection details";
  return "Connection";
}

function statusTone(
  status: IntegrationConnectionDetail["status"],
): "healthy" | "watch" | "critical" | "neutral" {
  if (status === "active") return "healthy";
  if (status === "pending") return "watch";
  if (status === "failed" || status === "expired") return "critical";
  return "neutral";
}

function statusLabel(status: IntegrationConnectionDetail["status"]): string {
  if (status === "active") return "Connected";
  if (status === "pending") return "Pending";
  if (status === "expired") return "Expired";
  if (status === "failed") return "Needs attention";
  return "Disconnected";
}

// Both go through the shell's shared formatter (PB-5134, d19): the relative
// wording is zone-free, its dated fallback and the drawer's absolute stamps
// render in the user's stored zone once one is saved, so the page never shows
// two zones.
function lastCheckedLabel(
  value: string | null,
  formatter: DateTimeFormatter,
): string {
  if (value === null) return "Not checked yet";
  return `Checked ${formatter.relative(value)}`;
}

function checkedTimestamp(
  value: string | null,
  formatter: DateTimeFormatter,
): string {
  if (value === null) return "Not available";
  return formatter.dateTime(value);
}

function splitIntegrationName(value: string): {
  name: string;
  context: string | null;
} {
  const separator = " — ";
  const index = value.indexOf(separator);
  if (index < 0) return { name: value, context: null };
  return {
    name: value.slice(0, index),
    context: value.slice(index + separator.length),
  };
}

/** Why Connect refused — see `connect-refusal.ts` for the reasoning. Kept
 *  as today's red line: Alert adds a glyph and its own type, a look change
 *  no PR 3 row approves. */
function ConnectRefusal({ error }: { error: unknown }): ReactElement {
  return (
    <p role="alert" className="text-sm text-pb-red-text">
      {connectRefusalMessage(error)}
    </p>
  );
}
